/**
 * Create and edit a store.
 *
 * The body is validated against `storeUpsertSchema` — the same schema the API
 * validates it with — before anything is sent, so a bad timezone is caught while
 * the person is still looking at the field rather than as a 400 a second later.
 * The opening-hours overlap check is the panel's own: the API accepts overlapping
 * entries because the resolver unions them harmlessly, but two entries covering
 * the same hour are almost always a typo worth stopping.
 */

import { useId, useMemo, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { storeUpsertSchema } from '@pbtv/shared';
import type { StoreUpsert } from '@pbtv/shared';
import { errorMessage } from '../../lib/api.js';
import { fromWeek, toWeek, weekIssues } from '../../lib/openingHours.js';
import type { DayHours } from '../../lib/openingHours.js';
import type { StoreRecord } from '../../lib/schemas.js';
import { DEFAULT_TIMEZONE, describeTimezone, timezoneOptions } from '../../lib/timezones.js';
import { OpeningHoursEditor } from './OpeningHoursEditor.js';

export interface StoreFormProps {
  /** Null when creating. */
  initial: StoreRecord | null;
  /** Brands already in use, offered as suggestions so spellings stay consistent. */
  knownBrands: readonly string[];
  busy: boolean;
  error: unknown;
  onSubmit: (value: StoreUpsert) => void;
  onCancel: () => void;
}

type FieldErrors = Partial<Record<'name' | 'brand' | 'timezone' | 'address', string>>;

export function StoreForm({
  initial,
  knownBrands,
  busy,
  error,
  onSubmit,
  onCancel,
}: StoreFormProps): ReactElement {
  const [name, setName] = useState(initial?.name ?? '');
  const [brand, setBrand] = useState(initial?.brand ?? '');
  const [timezone, setTimezone] = useState(initial?.timezone ?? DEFAULT_TIMEZONE);
  const [address, setAddress] = useState(initial?.address ?? '');
  const [week, setWeek] = useState<DayHours[]>(() => toWeek(initial?.openingHours ?? []));
  const [updateStart, setUpdateStart] = useState(initial?.updateWindow?.start ?? '');
  const [updateEnd, setUpdateEnd] = useState(initial?.updateWindow?.end ?? '');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const brandListId = useId();
  const timezoneListId = useId();

  const zones = useMemo(() => timezoneOptions(), []);
  const brands = useMemo(() => {
    const set = new Set(knownBrands.filter((value) => value.trim() !== ''));
    set.add('Pizza Bros');
    return [...set].sort((left, right) => left.localeCompare(right));
  }, [knownBrands]);

  const hoursIssues = useMemo(() => weekIssues(week), [week]);
  const timezoneReading = useMemo(() => describeTimezone(timezone, Date.now()), [timezone]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (hoursIssues.length > 0) return;

    const parsed = storeUpsertSchema.safeParse({
      name: name.trim(),
      brand: brand.trim(),
      timezone: timezone.trim(),
      address: address.trim(),
      openingHours: fromWeek(week),
      // Both blank means no window, which is how a store opts out of unattended
      // prompting entirely — an update there can then only be sent with an explicit
      // "now", by somebody already on the telephone to the shop.
      updateWindow:
        updateStart.trim() === '' || updateEnd.trim() === ''
          ? null
          : { start: updateStart.trim(), end: updateEnd.trim() },
    });

    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === 'name' || field === 'brand' || field === 'timezone' || field === 'address') {
          next[field] ??= issue.message;
        }
      }
      setFieldErrors(next);
      return;
    }

    setFieldErrors({});
    onSubmit(parsed.data);
  };

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <div className="form__grid">
        <label className="field">
          <span className="field__label">Store name</span>
          <input
            className="input"
            type="text"
            required
            autoFocus
            maxLength={120}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          {fieldErrors.name !== undefined ? (
            <span className="field__error">{fieldErrors.name}</span>
          ) : null}
        </label>

        <label className="field">
          <span className="field__label">Brand</span>
          <input
            className="input"
            type="text"
            required
            maxLength={40}
            list={brandListId}
            value={brand}
            onChange={(event) => setBrand(event.currentTarget.value)}
          />
          <datalist id={brandListId}>
            {brands.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          <span className="field__hint">
            Shared media can be limited to a brand, so keep the spelling identical across stores.
          </span>
          {fieldErrors.brand !== undefined ? (
            <span className="field__error">{fieldErrors.brand}</span>
          ) : null}
        </label>

        <label className="field">
          <span className="field__label">Timezone</span>
          <input
            className="input"
            type="text"
            required
            list={timezoneListId}
            value={timezone}
            onChange={(event) => setTimezone(event.currentTarget.value)}
          />
          <datalist id={timezoneListId}>
            {zones.map((zone) => (
              <option key={zone} value={zone} />
            ))}
          </datalist>
          <span className="field__hint">
            {timezoneReading}. Every schedule and every opening-hours check for this store’s screens
            is made in this zone, on the TV as well as here.
          </span>
          {fieldErrors.timezone !== undefined ? (
            <span className="field__error">{fieldErrors.timezone}</span>
          ) : null}
        </label>

        <label className="field">
          <span className="field__label">Address</span>
          <input
            className="input"
            type="text"
            maxLength={300}
            value={address}
            onChange={(event) => setAddress(event.currentTarget.value)}
          />
          {fieldErrors.address !== undefined ? (
            <span className="field__error">{fieldErrors.address}</span>
          ) : null}
        </label>
      </div>

      <OpeningHoursEditor value={week} onChange={setWeek} issues={hoursIssues} disabled={busy} />

      <div className="field field--span">
        <span className="field__label">Update window</span>
        <div className="field__row">
          <input
            className="input"
            type="time"
            value={updateStart}
            disabled={busy}
            aria-label="Update window start"
            onChange={(event) => setUpdateStart(event.currentTarget.value)}
          />
          <span aria-hidden="true">to</span>
          <input
            className="input"
            type="time"
            value={updateEnd}
            disabled={busy}
            aria-label="Update window end"
            onChange={(event) => setUpdateEnd(event.currentTarget.value)}
          />
        </div>
        <span className="field__hint">
          The hour of the day this store&rsquo;s TVs may be interrupted by an app update.
          Installing one puts a box on the screen that somebody has to answer, so choose a
          quiet time — but keep it <strong>inside opening hours</strong>: a screen that is
          not expected to be up raises no alert, so an update that went wrong overnight
          would be found by staff in the morning rather than by you. Leave both blank to
          allow updates only when you send one explicitly.
        </span>
      </div>

      {error !== null && error !== undefined ? (
        <p className="alert alert--danger" role="alert">
          {errorMessage(error)}
        </p>
      ) : null}

      <div className="form__actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="submit"
          className="button button--primary"
          disabled={busy || hoursIssues.length > 0}
        >
          {busy ? 'Saving…' : initial === null ? 'Create store' : 'Save changes'}
        </button>
      </div>

      {hoursIssues.length > 0 ? (
        <p className="form__blocked" role="status">
          Fix the opening hours above before saving.
        </p>
      ) : null}
    </form>
  );
}
